import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ComponentState } from '@/runtime/types/componentState';

export const useStateStore = <T>() =>
  defineStore('storedProps', () => {
    const state = ref<Record<string, ComponentState<T>>>({});

    function setState(key: string, componentState: ComponentState<T>) {
      const existing = state.value[key] as ComponentState<T> | undefined;
      if (existing) {
        // mutate to preserve identity
        existing.type = (componentState as any).type;
        existing.parent = (componentState as any).parent;
        if (existing.props) {
          Object.assign(existing.props as any, componentState.props as any);
        } else {
          (existing as any).props = componentState.props as any;
        }
      } else {
        state.value[key] = componentState;
      }
    }

    function findInState(pred: (v: any) => boolean) {
      for (const v of Object.values(state.value)) if (pred(v)) return v;
      return null;
    }

    function setPropValue<K extends keyof T>(uid: string | number, property: K, value: T[K]) {
      if (state.value[uid]) {
        state.value[uid].props[property] = value;
      }
    }
    function getByUid(id: string | number) {
      return state.value[String(id)] as ComponentState<any> | undefined;
    }

    function findParent(uid: string | number) {
      const current = getByUid(uid);
      if (!current?.parent) return undefined;
      return getByUid(current.parent);
    }

    function findParentOfType(uid: string | number, type: string) {
      const visited = new Set<string>();
      let node = findParent(uid);

      while (node) {
        const id = String(node.uid);
        if (visited.has(id)) break; // guard against cycles
        visited.add(id);

        const nodeType = (node as any).type ?? (node as any).typeName;
        if (nodeType === type) return node;

        if (!node.parent) return undefined;
        node = getByUid(node.parent);
      }
      return undefined;
    }

    function findChildren(uid: string | number) {
      const u = String(uid);
      const result: Array<ComponentState<unknown>> = [];
      for (const v of Object.values(state.value)) {
        if (v?.parent != null && String(v.parent) === u) {
          result.push(v as ComponentState<unknown>);
        }
      }

      return result;
    }

    function findChildrenByType(uid: string | number, type: string) {
      const children = findChildren(uid);
      return children.filter(child => {
        return child.type === type;
      });
    }

    function clear() {
      // preserve map identity to avoid breaking watchers
      const map = state.value;
      for (const k of Object.keys(map)) {
        Reflect.deleteProperty(map, k);
      }
    }
    return {
      state,
      setState,
      setPropValue,
      findInState,
      getByUid,
      findParent,
      findParentOfType,
      findChildren,
      findChildrenByType,
      clear
    };
  })();
